import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges
} from '@angular/core';
import type { PrivacyOption } from 'go-modules/activity-editor/activity-editor.controller';
import { PrivacyLevel } from 'go-modules/models/activity';
import { ActivityTemplateModelType, activityTemplateModelToken } from 'go-modules/models/activity-template/activity-template.factory';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import type { ActivityFactory } from 'go-modules/models/activity/activity.schema';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subject, Subscription, debounceTime, distinctUntilChanged, filter, first, forkJoin, from, takeUntil, catchError, finalize, tap, EMPTY } from 'rxjs';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { tagSetModelToken } from 'go-modules/models/tag-set/tag-set.factory';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { MatDialog } from '@angular/material/dialog';
import { DefaultAIMarkersDialogComponent, DefaultAiMarkersDialogData, DefaultAiMarkersDialogResultData } from '@go-modules/components/dialogs/default-ai-markers-dialog/default-ai-markers-dialog.component';
import { LlmDefinition } from '@go-modules/interfaces/llm-definition';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LibraryDialogComponent } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';

export const DEBOUNCE_TIME = 500;
@Component({
	selector: 'default-activity-settings',
	templateUrl: './default-activity-settings.component.html',
	styleUrls: ['./default-activity-settings.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class DefaultActivitySettingsComponent implements OnChanges, OnDestroy {
	@Input() public account;

	public activityTemplates = [];
	public privacyTypeLookup = null;
	public privacyTypes = [];
	public filteredPrivacyTypes = [];

	public activityForm: UntypedFormGroup;
	public valueChangeSubscription: Subscription;
	public defaultActivity$$ = new BehaviorSubject(null);
	public showSaveButton$ = new BehaviorSubject(false);
	public isSaving$ = new BehaviorSubject(false);
	public selectedMarkerSet: any;
	public selectedAiUseType: number;
	public selectedAiUseTypeName: string;
	public defaultLlmDefinitions: LlmDefinition[];

	private templateOrder = [
		ActivityTemplateModelType.STANDARD,
		ActivityTemplateModelType.STIMULUS_RESPONSE,
		ActivityTemplateModelType.FEEDBACK_ONLY
	];

	private destroyed$$ = new Subject();

	constructor (
		public featureFlag: NgxFeatureFlagService,
		private activityService: NgxActivityService,
		private fb: UntypedFormBuilder,
		private ngxGoToastService: NgxGoToastService,
		private cdr: ChangeDetectorRef,
		private dialog: MatDialog,
		private modal: GoModalService,
		@Inject(activityToken) private activityModel: ActivityFactory,
		@Inject(activityTemplateModelToken) private activityTemplateModel,
		@Inject(tagSetModelToken) public tagSetModel)
	{
		const activity = this.defaultActivity$$.asObservable()
			.pipe(takeUntil(this.destroyed$$), filter((item) => !!item), first());

		forkJoin([activity, from(this.activityTemplateModel.query({}).$promise)])
			.pipe(takeUntil(this.destroyed$$))
			.subscribe(([, templates]: [any, any[]]) => {
				this.activityTemplates = templates.sort((a, b) =>
					this.templateOrder.indexOf(a.getType()) - this.templateOrder.indexOf(b.getType())
				);
			});
	}

	public ngOnDestroy (): void {
		this.destroyed$$.next(true);
		this.destroyed$$.complete();
		this.valueChangeSubscription?.unsubscribe();
	}

	public initForm () {
		this.generatePrivacyLists(this.defaultActivity$$.getValue().template);
		let template = this.defaultActivity$$.getValue().template;
		// Some account does not have a use_type.
		// In that case set make standard as default.
		if (!template.slug) {
			template = this.activityTemplates
				.find((value) => value.getType() === ActivityTemplateModelType.STANDARD);
		}

		const isFeedbackPublished = this.defaultActivity$$.getValue().is_feedback_published;
		const privacy = this.determineInitiallySelectedPrivacySetting();

		this.activityForm = this.fb.group({
			template: [template, Validators.required],
			privacyType: [privacy, Validators.required],
			is_feedback_published: [isFeedbackPublished, Validators.required]
		});

		this.valueChangeSubscription?.unsubscribe();
		this.valueChangeSubscription = this.activityForm.valueChanges
			.pipe(
				debounceTime(DEBOUNCE_TIME),
				distinctUntilChanged()
			).subscribe((value) => {
				const templateChanged = value.template !== this.defaultActivity$$.getValue().template;
				const isFeedbackPublishedChanged =
					value.is_feedback_published !== this.defaultActivity$$.getValue().is_feedback_published;
				const privacyTypeChanged = value.privacyType !== this.determineInitiallySelectedPrivacySetting();
				this.showSaveButton$.next(templateChanged || isFeedbackPublishedChanged || privacyTypeChanged);
			});

		this.determineInitiallySelectedMarkerSet();
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.account && this.account) {
			this.getDefaultActivityModel();
		}
	}

	public getDefaultActivityModel () {
		this.activityService.getDefault()
			.subscribe((result) => {
				this.defaultActivity$$.next(this.activityModel.model(result.activity));
				this.defaultLlmDefinitions = result.aiUseType?.defaultLlmDefinitions;
				this.selectedAiUseType = result.aiUseType?.use_type_id;
				this.selectedAiUseTypeName = result.aiUseType?.name;
				this.initForm();
			});
	}

	public isAiCompatible (): boolean {
		return this.featureFlag.isAvailable('AI_DEFAULT') &&
			this.activityForm.controls.template.value.ai_prompts_enabled === 'active';
	}

	public onActivityValueChanged () {
		this.generatePrivacyLists();
		this.activityForm.patchValue({
			privacyType: null
		});
	}

	/**
	 * Determines the privacy setting that should initially be selected
	 */
	private determineInitiallySelectedPrivacySetting (): PrivacyOption {
		switch (this.defaultActivity$$.getValue().getPrivacyLevel()) {
			case PrivacyLevel.CLOSED_PEER_REVIEW:
				return this.privacyTypeLookup.CLOSED_PEER_REVIEW;
			case PrivacyLevel.OPEN_PEER_REVIEW:
			case PrivacyLevel.OPEN_FEEDBACK:
				return this.privacyTypeLookup.EVERYONE_SEES_EVERYTHING;
			case PrivacyLevel.PRIVATE:
			default:
				return this.privacyTypeLookup.PRIVATE;
		}
	}

	/**
	 * Defines and updates what is shown in the privacy settings dropdown
	 */
	public generatePrivacyLists (template?): void {
		const currentTemplate = template ??
			this.activityForm.get('template').value;

		const isActive = currentTemplate.isActive('has_response_media');

		this.privacyTypeLookup = {
			PRIVATE: isActive ?
				{
					title: 'activity-editor-new_private',
					description: 'activity-editor-new_private-description',
					peerReview: false,
					publicFeedback: false
				} :
				{
					title:
					'activity-editor-new_private',
					description: 'activity-editor-new_private-description-filtered',
					peerReview: false,
					publicFeedback: false
				},
			CLOSED_PEER_REVIEW:
				{
					title: 'activity-editor-new_closed-peer-review',
					description: 'activity-editor-new_closed-peer-review-description',
					peerReview: true,
					publicFeedback: false
				},
			EVERYONE_SEES_EVERYTHING: isActive ?
				{
					title: 'activity-editor-new_everyone-sees-everything',
					description: 'activity-editor-new_everyone-sees-everything-description',
					peerReview: true,
					publicFeedback: true
				} :
				{
					title: 'activity-editor-new_everyone-sees-everything',
					description: 'activity-editor-new_everyone-sees-everything-description-filtered',
					peerReview: false,
					publicFeedback: true
				}
		};

		this.privacyTypes = isActive ? [
			this.privacyTypeLookup.PRIVATE,
			this.privacyTypeLookup.CLOSED_PEER_REVIEW,
			this.privacyTypeLookup.EVERYONE_SEES_EVERYTHING
		] : [
			this.privacyTypeLookup.PRIVATE,
			this.privacyTypeLookup.EVERYONE_SEES_EVERYTHING
		];
	}

	public save () {
		if(this.activityForm.invalid) {
			this.activityForm.markAllAsTouched();
			return;
		}

		this.isSaving$.next(true);
		const activity = this.defaultActivity$$.getValue();
		activity.template = this.activityForm.value.template;
		activity.activity_template_id = this.activityForm.value.template.id;
		activity.is_feedback_published = this.activityForm.value.is_feedback_published;
		activity.public_feedback = this.activityForm.value.privacyType.publicFeedback;
		activity.peer_review =  this.activityForm.value.privacyType.peerReview;
		activity.tag_set_id = this.selectedMarkerSet?.id ?? null;
		activity.source_media = null;

		const saveActivity$ = this.activityService.update(activity);
		const saveAiMarkers$ = this.activityService.updateAiMarkersDefault(
			this.isAiCompatible() ? this.selectedAiUseType : null
		);

		forkJoin({
			saveActivity: saveActivity$,
			saveAiMarkers: saveAiMarkers$
		}).pipe(
			catchError(() => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'default-activity-settings_error-message'
				});
				return EMPTY;
			}),
			tap(() => {
				this.showSaveButton$.next(false);
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'common-save-success'
				});
			}),
			finalize(() => this.isSaving$.next(false))
		).subscribe();
	}

	public openMarkerSelectionModal (): void {
		this.modal.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.MARKERS],
				mode: MODES.SELECT,
				collectionManagement: true
			}
		}).afterClosed().subscribe((markers) => {
			if (markers) {
				this.selectedMarkerSet = new this.tagSetModel(markers);
				this.showSaveButton$.next(true);
				this.cdr.detectChanges();
			}
		});
	}

	public handleMarkerSetEdited ({markerSet}): void {
		this.selectedMarkerSet = markerSet;
		this.showSaveButton$.next(true);
	}

	public handleMarkerSetRemoved (): void {
		this.selectedMarkerSet = null;
		this.showSaveButton$.next(true);
	}

	public openAiMarkerSelectionModal (mode = 'add') {
		const defaultUseTypeId = mode === 'add' ? null : this.selectedAiUseType;
		this.dialog.open(DefaultAIMarkersDialogComponent, {
			data: {
				mode,
				useTypeId: defaultUseTypeId
			} as DefaultAiMarkersDialogData
		}).afterClosed().subscribe((result: DefaultAiMarkersDialogResultData) => {
			if (result) {
				this.defaultLlmDefinitions = result.llmDefinitions;
				this.selectedAiUseType = result.useTypeId;
				this.selectedAiUseTypeName = result.useTypeName;
				this.showSaveButton$.next(true);
			}
		});
	}

	public handleAiMarkerSetRemove () {
		this.defaultLlmDefinitions = [];
		this.selectedAiUseType = null;
		this.showSaveButton$.next(true);
	}

	private determineInitiallySelectedMarkerSet (): any {
		const activity = this.defaultActivity$$.getValue();
		if (activity.tag_set_id > 0) {
			this.selectedMarkerSet = this.tagSetModel.get({
				id: activity.tag_set_id
			});
		}
	}
}
